import React, { useMemo, useState } from "react";
import moment from "moment";
import { usePostProgramActivationMutation } from "../../../utils/api/endpoints/programsApi";
import ProgramIcon from "../../../assets/icons/act_require_prog.svg";
import toast from "react-hot-toast";

type Urgency = "today" | "urgent" | "high" | "needs";

const urgencyForDays = (days: number): Urgency | undefined => {
  if (days === 0) return "today";   // Starting today — highest upcoming priority
  if (days === 1) return "urgent";
  if (days === 2) return "high";
  if (days >= 3 && days <= 5) return "needs";
  return undefined;
};

const UrgencyBadge = ({ kind }: { kind: Urgency }) => {
  const map = {
    today: { label: "Starting Today", cls: "bg-purple-100 text-purple-800" },
    urgent: { label: "Urgent", cls: "bg-red-100 text-red-700" },
    high: { label: "High Priority", cls: "bg-orange-100 text-orange-700" },
    needs: { label: "Needs Validation", cls: "bg-blue-100 text-blue-700" }
  } as const;
  const { label, cls } = map[kind];
  return <span className={`rounded-md px-2 py-1 text-xs font-medium ${cls}`}>{label}</span>;
};

const borderColorForUrgency = (kind?: Urgency) => {
  if (kind === "today") return "#7C3AED"; // purple-600
  if (kind === "urgent") return "#EF4444";
  if (kind === "high") return "#F97316";
  if (kind === "needs") return "#3B82F6";
  return "#E5E7EB";
};

type Props = {
  items?: Array<{
    programId: number;
    programTitle: string;
    expectedStartDate: string | null;
    daysUntil: number;
    isOverdue?: boolean;
  }>;
};

type PriorityFilter = "all" | "overdue" | "today" | "urgent" | "high" | "needs";

const ActionRequiredList: React.FC<Props> = ({ items: serverItems }) => {
  const [mutate, { isLoading }] = usePostProgramActivationMutation();
  const [busyId, setBusyId] = useState<number | null>(null);
  const [itemsState, setItemsState] = useState(serverItems || []);
  const [removedIds, setRemovedIds] = useState<number[]>([]);
  const [priorityFilter, setPriorityFilter] = useState<PriorityFilter>("all");

  const { items, overdueCount } = useMemo(() => {
    const source = (serverItems && serverItems.length > 0) ? serverItems : itemsState;
    const filtered = (source || []).filter(p => !removedIds.includes(p.programId));

    // Overdue bucket (already past start date)
    const overdueRows = filtered
      .filter(p => p.isOverdue)
      .map(p => ({
        id: p.programId,
        title: p.programTitle,
        days: p.daysUntil,
        urgency: undefined as Urgency | undefined,
        isOverdue: true as const,
        daysOverdue: Math.abs(p.daysUntil)
      }));

    // Upcoming bucket: today (0) through +5 days
    const upcomingRows = filtered
      .filter(p => !p.isOverdue)
      .map(p => ({
        id: p.programId,
        title: p.programTitle,
        days: p.daysUntil,
        urgency: urgencyForDays(p.daysUntil),
        isOverdue: false as const,
        daysOverdue: 0
      }));

    // Sort order: today (0) → urgent (1) → high (2) → needs (3+)
    const rank: Record<Urgency, number> = { today: 0, urgent: 1, high: 2, needs: 3 };
    const sortedUpcoming = upcomingRows
      .filter(r => r.urgency !== undefined)
      .sort((a, b) => (rank[a.urgency as Urgency] - rank[b.urgency as Urgency]) || a.days - b.days);

    let items: (typeof overdueRows[number] | typeof sortedUpcoming[number])[];
    if (priorityFilter === "overdue") items = overdueRows;
    else if (priorityFilter === "today") items = sortedUpcoming.filter(i => i.urgency === "today");
    else if (priorityFilter === "all") items = [...overdueRows, ...sortedUpcoming];
    else items = sortedUpcoming.filter(item => item.urgency === priorityFilter);

    return { items, overdueCount: overdueRows.length };
  }, [serverItems, itemsState, removedIds, priorityFilter]);

  const onChange = async (id: number, makeActive: boolean) => {
    try {
      setBusyId(id);
      await mutate({ id, isActivated: makeActive }).unwrap();
      if (makeActive) {
        setRemovedIds(prev => [...prev, id]);
        setItemsState(prev => prev.filter(p => p.programId !== id));
        toast.success("Program activated");
      } else {
        toast.success("Program updated");
      }
    } catch (e) {
      toast.error("Failed to update program status");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4 h-[480px] flex flex-col">
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <h3 className="text-base font-semibold text-gray-800">Action Required</h3>
          {overdueCount > 0 && (
            <span className="rounded-full bg-red-900 px-2 py-0.5 text-xs font-bold text-white">
              {overdueCount} overdue
            </span>
          )}
        </div>
        <select
          value={priorityFilter}
          onChange={e => setPriorityFilter(e.target.value as PriorityFilter)}
          className="rounded-md px-4 py-2.5 text-sm appearance-none bg-white pr-8 focus:outline-none"
          style={{
            borderColor: "#D1D5DB",
            color: "#6B7280",
            borderWidth: "1px",
            backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%236B7280' d='M6 9L2 5h8z'/%3E%3C/svg%3E")`,
            backgroundRepeat: "no-repeat",
            backgroundPosition: "right 0.75rem center",
            backgroundSize: "12px 12px"
          }}
        >
          <option value="all">All</option>
          <option value="overdue">Overdue</option>
          <option value="today">Starting Today</option>
          <option value="urgent">Urgent</option>
          <option value="high">High Priority</option>
          <option value="needs">Needs Validation</option>
        </select>
      </div>

      {items.length === 0 ? (
        <div className="text-sm text-gray-500">No programs need attention.</div>
      ) : (
        <div className="space-y-3 overflow-y-auto pr-1" style={{ maxHeight: "calc(480px - 44px)" }}>
          {items.map(item => (
            <div
              key={item.id}
              className="flex items-center justify-between rounded-none border border-gray-100 p-4"
              style={{
                backgroundColor: item.isOverdue ? "#FEF2F2" : item.urgency === "today" ? "#FAF5FF" : "#F9FAFB",
                borderLeft: `4px solid ${item.isOverdue ? "#7F1D1D" : borderColorForUrgency(item.urgency)}`
              }}
            >
              <div className="min-w-0">
                <div className="mb-1 flex items-center gap-2 text-gray-600">
                  <img src={ProgramIcon} alt="Program" className="h-3 w-3" />
                  <span className="text-xs font-medium">Program</span>
                </div>
                <div className="truncate text-base font-medium text-gray-900">{item.title}</div>
                <div className="mt-2 flex items-center gap-2">
                  {item.isOverdue ? (
                    <>
                      <span className="rounded-md bg-red-900 px-2 py-1 text-xs font-medium text-white">
                        ⚠ Overdue
                      </span>
                      <span className="text-xs text-red-700">
                        {(item as any).daysOverdue === 0
                          ? "Was due today"
                          : `${(item as any).daysOverdue} day${(item as any).daysOverdue !== 1 ? "s" : ""} past start date`}
                      </span>
                    </>
                  ) : (
                    item.urgency && <UrgencyBadge kind={item.urgency} />
                  )}
                </div>
              </div>
              <select
                disabled={isLoading || busyId === item.id}
                defaultValue=""
                className="rounded-md px-3 py-2 text-sm font-medium shadow-sm focus:outline-none appearance-none pr-8"
                style={{
                  backgroundColor: "#010D2C",
                  color: "#FFFFFF",
                  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23FFFFFF' d='M6 9L2 5h8z'/%3E%3C/svg%3E")`,
                  backgroundRepeat: "no-repeat",
                  backgroundPosition: "right 0.5rem center",
                  backgroundSize: "12px 12px"
                }}
                onChange={e => {
                  const v = e.target.value;
                  if (v === "activate") onChange(item.id, true);
                  if (v === "deactivate") onChange(item.id, false);
                  e.currentTarget.value = "";
                }}
              >
                <option value="" disabled>Select active status</option>
                <option value="activate">Activate</option>
                <option value="deactivate">Keep Inactive</option>
              </select>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ActionRequiredList;
